import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { Card, CardContent, CardHeader } from 'components/Card/Card';
import { Container } from 'components/Container/Container';
import { Icon } from 'components/Icon/Icon';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    INVOICE,
    INVOICES,
    INVOICE_STATS,
    INVOICE_APPROVE,
    INVOICE_REJECT,
    INVOICE_GENERATE_PREVIEW,
    INVOICE_SEND,
    INVOICE_MARK_PAID,
    INVOICE_CANCEL,
    INVOICE_SUBMIT_FOR_APPROVAL,
} from 'services/invoices';

const getStatusBadgeVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
        case 'DRAFT':
            return 'secondary';
        case 'PENDING_APPROVAL':
            return 'outline';
        case 'APPROVED':
            return 'default';
        case 'SENT':
            return 'default';
        case 'PAID':
            return 'default';
        case 'OVERDUE':
            return 'destructive';
        case 'CANCELLED':
            return 'secondary';
        case 'CREDITED':
            return 'secondary';
        case 'UNPAID':
            return 'outline';
        default:
            return 'default';
    }
};

const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(num);
};

const formatDiscount = (line: any) => {
    if (parseFloat(line.discountPercent) > 0) {
        return `${line.discountPercent}%`;
    }
    if (parseFloat(line.discountAmount) > 0) {
        return formatCurrency(line.discountAmount);
    }
    return '-';
};

const getTypeLabel = (type: string, t: (key: string) => string) => {
    switch (type) {
        case 'MEMBERSHIP':
            return t('invoices.typeMembership');
        case 'CUSTOM':
            return t('invoices.typeCustom');
        case 'CONSUMPTION':
            return t('invoices.typeConsumption');
        case 'NEGATIVE_BALANCE':
            return t('invoices.typeNegativeBalance');
        case 'TOP_UP':
            return t('invoices.typeTopUp');
        default:
            return type;
    }
};

export const InvoiceDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation('translation');
    const [actionLoading, setActionLoading] = useState(false);

    const { data, loading, refetch } = useQuery(INVOICE, {
        variables: { id },
        skip: !id,
    });

    const refetchQueries = [
        { query: INVOICE, variables: { id } },
        { query: INVOICES },
        { query: INVOICE_STATS },
    ];

    const [submitForApproval] = useMutation(INVOICE_SUBMIT_FOR_APPROVAL, { refetchQueries });
    const [approve] = useMutation(INVOICE_APPROVE, { refetchQueries });
    const [reject] = useMutation(INVOICE_REJECT, { refetchQueries });
    const [generatePreview] = useMutation(INVOICE_GENERATE_PREVIEW, { refetchQueries });
    const [send] = useMutation(INVOICE_SEND, { refetchQueries });
    const [markPaid] = useMutation(INVOICE_MARK_PAID, { refetchQueries });
    const [cancel] = useMutation(INVOICE_CANCEL, { refetchQueries });

    const handlePreview = async () => {
        setActionLoading(true);
        try {
            const result = await generatePreview({ variables: { id } });
            const previewUrl = result.data?.invoiceGeneratePreview?.previewUrl;
            const documentUrl = result.data?.invoiceGeneratePreview?.invoice?.document?.url;
            const urlToOpen = previewUrl || documentUrl;

            if (urlToOpen) {
                setTimeout(() => {
                    window.open(urlToOpen, '_blank', 'noopener,noreferrer');
                }, 100);
            } else {
                alert(t('invoices.pdfNotGenerated'));
            }
            await refetch();
        } catch (error) {
            console.error('Preview generation failed:', error);
            alert(t('invoices.previewFailed'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleAction = async (action: string) => {
        setActionLoading(true);
        try {
            switch (action) {
                case 'submit':
                    await submitForApproval({ variables: { id } });
                    break;
                case 'approve':
                    await approve({ variables: { id } });
                    break;
                case 'reject':
                    const rejectReason = window.prompt(t('invoices.enterRejectionReason'));
                    if (rejectReason) {
                        await reject({ variables: { id, reason: rejectReason } });
                    }
                    break;
                case 'send':
                    await send({ variables: { id, regeneratePdf: true } });
                    break;
                case 'mark_paid':
                    await markPaid({ variables: { id } });
                    break;
                case 'cancel':
                    const cancelReason = window.prompt(t('invoices.enterCancellationReason'));
                    if (cancelReason) {
                        await cancel({ variables: { id, reason: cancelReason } });
                    }
                    break;
                default:
                    break;
            }
            await refetch();
        } catch (error) {
            console.error('Action failed:', error);
            alert(t('invoices.actionFailed'));
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <div className="p-8 text-center">{t('invoices.loading')}</div>;
    }

    if (!data?.invoice) {
        return <div className="p-8 text-center">{t('invoices.invoiceNotFound')}</div>;
    }

    const invoice = data.invoice;
    const lines = invoice.lines || [];

    // For older invoices without line items, generate a synthetic line from the invoice total
    const hasRealLines = lines.length > 0;
    // Old invoices store tax in `tax` field; new ones compute `taxTotal` from line items
    const invoiceTaxTotal = parseFloat(invoice.taxTotal) || 0;
    const invoiceTaxLegacy = parseFloat(invoice.tax) || 0;
    const invoiceTax = invoiceTaxTotal > 0 ? invoiceTaxTotal : invoiceTaxLegacy;
    const invoiceAmount = parseFloat(invoice.amount) || 0;
    const invoiceSubtotal = parseFloat(invoice.subtotal) || 0;

    // For old invoices: subtotal may be 0 because no line items existed.
    // `amount` is always gross (= subtotal - discount + tax), legacy invoices included.
    const effectiveSubtotal = hasRealLines || invoiceSubtotal > 0
        ? invoiceSubtotal
        : invoiceAmount - invoiceTax;

    const syntheticLine = {
        id: 'synthetic',
        description: getTypeLabel(invoice.type, t),
        quantity: '1',
        unitPrice: effectiveSubtotal.toString(),
        discountPercent: '0',
        discountAmount: '0',
        taxRate: invoiceTax > 0 ? ((invoiceTax / effectiveSubtotal) * 100).toFixed(1) : '0',
        lineTotal: effectiveSubtotal.toString(),
    };
    let displayLines = lines;
    if (!hasRealLines && invoiceAmount > 0) {
        displayLines = [syntheticLine];
    }

    const discount =
        parseFloat(invoice.discountPercent) > 0
            ? effectiveSubtotal * (parseFloat(invoice.discountPercent) / 100)
            : parseFloat(invoice.discountAmount);

    const displaySubtotal = hasRealLines ? invoice.subtotal : effectiveSubtotal.toString();
    const displayTaxTotal = invoiceTax.toString();
    // `amount` already includes tax for all invoices (matches PDF: total_with_tax = invoice.amount).
    const displayTotal = invoiceAmount;

    return (
        <Container>
            <Breadcrumbs crumbs={[t('breadcrumbs.dashboard'), t('invoices.title'), invoice.invoiceId]} />

            <div className="flex justify-between items-start mb-6">
                <div>
                    <PageTitle withBack>
                        {t('invoices.title')} {invoice.invoiceId}
                    </PageTitle>
                    <div className="flex gap-2 items-center mt-2">
                        <Badge variant={getStatusBadgeVariant(invoice.status)} className="text-sm">
                            {t(`status.${invoice.status}`, { defaultValue: invoice.status })}
                        </Badge>
                        <Badge variant="outline">{getTypeLabel(invoice.type, t)}</Badge>
                    </div>
                </div>
                <div className="flex gap-2">
                    {invoice.isEditable && (
                        <Button variant="outline" onClick={() => navigate(`/invoices/${id}/edit`)}>
                            <Icon name="edit" className="mr-2 h-4 w-4" />
                            {t('invoices.edit')}
                        </Button>
                    )}
                    <Button variant="outline" onClick={handlePreview} disabled={actionLoading}>
                        <Icon name="eye" className="mr-2 h-4 w-4" />
                        {actionLoading ? t('invoices.generating') : t('invoices.previewPdf')}
                    </Button>
                    {invoice.document?.url && (
                        <a href={invoice.document.url} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline">
                                <Icon name="download" className="mr-2 h-4 w-4" />
                                {t('invoices.downloadPdf')}
                            </Button>
                        </a>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Invoice Details */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.invoiceDetails')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <div className="text-sm text-gray-500">{t('invoices.table.invoiceNumber')}</div>
                                    <div className="font-mono">{invoice.invoiceId}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">{t('invoices.issueDate')}</div>
                                    <div>
                                        {invoice.issueDate
                                            ? dayjs(invoice.issueDate).format('DD.MM.YYYY')
                                            : dayjs(invoice.createdAt).format('DD.MM.YYYY')}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">{t('invoices.dueDate')}</div>
                                    <div>{dayjs(invoice.dueDate).format('DD.MM.YYYY')}</div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500">{t('invoices.type')}</div>
                                    <div>{getTypeLabel(invoice.type, t)}</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Recipient */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.recipient')}</h3>
                        </CardHeader>
                        <CardContent>
                            {(() => {
                                if (invoice.company) {
                                    return (
                                        <div>
                                            <div className="font-medium">{invoice.company.name}</div>
                                            <div className="text-sm text-gray-500">{t('invoices.company')}</div>
                                            <Link
                                                to={`/companies/${invoice.company.id}`}
                                                className="text-rose-600 text-sm hover:underline"
                                            >
                                                {t('invoices.viewCompany')}
                                            </Link>
                                        </div>
                                    );
                                }
                                if (invoice.member) {
                                    return (
                                        <div>
                                            <div className="font-medium">
                                                {invoice.member.firstName} {invoice.member.lastName}
                                            </div>
                                            <div className="text-sm text-gray-500">{invoice.member.email}</div>
                                            <Link
                                                to={`/members/${invoice.member.id}`}
                                                className="text-rose-600 text-sm hover:underline"
                                            >
                                                {t('invoices.viewMember')}
                                            </Link>
                                        </div>
                                    );
                                }
                                return <div className="text-gray-500">{t('invoices.noRecipient')}</div>;
                            })()}
                        </CardContent>
                    </Card>

                    {/* Line Items */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">
                                {t('invoices.lineItems')} ({displayLines.length})
                            </h3>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('invoices.description')}</TableHead>
                                            <TableHead className="text-right">{t('invoices.quantity')}</TableHead>
                                            <TableHead className="text-right">{t('invoices.unitPrice')}</TableHead>
                                            <TableHead className="text-right">{t('invoices.discount')}</TableHead>
                                            <TableHead className="text-right">{t('invoices.tax')}</TableHead>
                                            <TableHead className="text-right">{t('invoices.total')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {displayLines.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                                    {t('invoices.noLineItemsDetail')}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            displayLines.map((line: any) => (
                                                <TableRow key={line.id}>
                                                    <TableCell>{line.description}</TableCell>
                                                    <TableCell className="text-right">
                                                        {parseFloat(line.quantity).toFixed(0)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatCurrency(line.unitPrice)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatDiscount(line)}
                                                    </TableCell>
                                                    <TableCell className="text-right">{line.taxRate}%</TableCell>
                                                    <TableCell className="text-right font-medium">
                                                        {formatCurrency(line.lineTotal)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Notes */}
                    {(invoice.headerNotes || invoice.footerNotes || invoice.internalNotes) && (
                        <Card>
                            <CardHeader>
                                <h3 className="text-lg font-semibold">{t('invoices.notes')}</h3>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {invoice.headerNotes && (
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">
                                            {t('invoices.headerNotes')}
                                        </div>
                                        <div className="whitespace-pre-wrap">{invoice.headerNotes}</div>
                                    </div>
                                )}
                                {invoice.footerNotes && (
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">
                                            {t('invoices.footerNotes')}
                                        </div>
                                        <div className="whitespace-pre-wrap">{invoice.footerNotes}</div>
                                    </div>
                                )}
                                {invoice.internalNotes && (
                                    <div>
                                        <div className="text-sm font-medium text-gray-500">
                                            {t('invoices.internalNotes')}
                                        </div>
                                        <div className="whitespace-pre-wrap bg-yellow-50 p-2 rounded">
                                            {invoice.internalNotes}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Totals */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.totals')}</h3>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>{t('invoices.subtotal')}</span>
                                    <span>{formatCurrency(displaySubtotal)}</span>
                                </div>
                                {discount > 0 && (
                                    <div className="flex justify-between text-rose-600">
                                        <span>
                                            {t('invoices.discount')}
                                            {invoice.discountDescription && ` (${invoice.discountDescription})`}
                                            {parseFloat(invoice.discountPercent) > 0 &&
                                                ` (${invoice.discountPercent}%)`}
                                        </span>
                                        <span>-{formatCurrency(discount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span>{t('invoices.tax')}</span>
                                    <span>{formatCurrency(displayTaxTotal)}</span>
                                </div>
                                <hr className="my-2" />
                                <div className="flex justify-between text-lg font-bold">
                                    <span>{t('invoices.total')}</span>
                                    <span>{formatCurrency(displayTotal)}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Workflow Actions */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('actions')}</h3>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            {invoice.status === 'DRAFT' && (
                                <>
                                    <Button
                                        className="w-full"
                                        onClick={() => handleAction('submit')}
                                        disabled={actionLoading || lines.length === 0}
                                    >
                                        {t('invoices.submitForApproval')}
                                    </Button>
                                    <Button
                                        className="w-full"
                                        variant="outline"
                                        onClick={() => navigate(`/invoices/${id}/edit`)}
                                    >
                                        {t('invoices.editInvoice')}
                                    </Button>
                                </>
                            )}

                            {invoice.status === 'PENDING_APPROVAL' && (
                                <>
                                    <Button
                                        className="w-full"
                                        onClick={() => handleAction('approve')}
                                        disabled={actionLoading}
                                    >
                                        {t('invoices.approve')}
                                    </Button>
                                    <Button
                                        className="w-full"
                                        variant="outline"
                                        onClick={() => handleAction('reject')}
                                        disabled={actionLoading}
                                    >
                                        {t('invoices.rejectReturnDraft')}
                                    </Button>
                                </>
                            )}

                            {invoice.status === 'APPROVED' && (
                                <>
                                    <Button
                                        className="w-full"
                                        onClick={() => handleAction('send')}
                                        disabled={actionLoading}
                                    >
                                        {t('invoices.sendInvoice')}
                                    </Button>
                                    <Button
                                        className="w-full"
                                        variant="destructive"
                                        onClick={() => handleAction('cancel')}
                                        disabled={actionLoading}
                                    >
                                        {t('invoices.cancelInvoice')}
                                    </Button>
                                </>
                            )}

                            {(invoice.status === 'SENT' ||
                                invoice.status === 'UNPAID' ||
                                invoice.status === 'OVERDUE') && (
                                <>
                                    <Button
                                        className="w-full"
                                        onClick={() => handleAction('mark_paid')}
                                        disabled={actionLoading}
                                    >
                                        {t('invoices.markAsPaid')}
                                    </Button>
                                    <Button
                                        className="w-full"
                                        variant="destructive"
                                        onClick={() => handleAction('cancel')}
                                        disabled={actionLoading}
                                    >
                                        {t('invoices.cancelInvoice')}
                                    </Button>
                                </>
                            )}

                            {invoice.status === 'PAID' && (
                                <div className="text-center text-green-600 py-4">
                                    <Icon name="check" className="h-8 w-8 mx-auto mb-2" />
                                    <div className="font-medium">{t('invoices.invoicePaid')}</div>
                                    {invoice.paidAt && (
                                        <div className="text-sm text-gray-500">
                                            {dayjs(invoice.paidAt).format('DD.MM.YYYY HH:mm')}
                                        </div>
                                    )}
                                </div>
                            )}

                            {invoice.status === 'CANCELLED' && (
                                <div className="text-center text-gray-500 py-4">
                                    <Icon name="close" className="h-8 w-8 mx-auto mb-2" />
                                    <div className="font-medium">{t('invoices.invoiceCancelled')}</div>
                                    {invoice.cancellationReason && (
                                        <div className="text-sm mt-2">
                                            {t('invoices.reason')}: {invoice.cancellationReason}
                                        </div>
                                    )}
                                </div>
                            )}

                            <Button className="w-full" variant="outline" onClick={() => navigate('/invoices')}>
                                {t('invoices.backToList')}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Audit Info */}
                    <Card>
                        <CardHeader>
                            <h3 className="text-lg font-semibold">{t('invoices.auditInfo')}</h3>
                        </CardHeader>
                        <CardContent className="text-sm space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('invoices.created')}</span>
                                <span>{dayjs(invoice.createdAt).format('DD.MM.YYYY HH:mm')}</span>
                            </div>
                            {invoice.createdBy && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{t('invoices.createdBy')}</span>
                                    <span>
                                        {invoice.createdBy.firstName} {invoice.createdBy.lastName}
                                    </span>
                                </div>
                            )}
                            {invoice.approvedBy && (
                                <>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('invoices.approvedBy')}</span>
                                        <span>
                                            {invoice.approvedBy.firstName} {invoice.approvedBy.lastName}
                                        </span>
                                    </div>
                                    {invoice.approvedAt && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">{t('invoices.approvedAt')}</span>
                                            <span>{dayjs(invoice.approvedAt).format('DD.MM.YYYY HH:mm')}</span>
                                        </div>
                                    )}
                                </>
                            )}
                            {invoice.sentAt && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{t('invoices.sentAt')}</span>
                                    <span>{dayjs(invoice.sentAt).format('DD.MM.YYYY HH:mm')}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('invoices.lastUpdated')}</span>
                                <span>{dayjs(invoice.updatedAt).format('DD.MM.YYYY HH:mm')}</span>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </Container>
    );
};
